// legal-core.js - the shared substrate for the GDBS legal plugin suite.
//
// WHY THIS EXISTS. Every legal plugin (deadline calc, SOL lookup, court math, the Pro engines,
// the HPC batch tools) rests on the SAME primitives: exact calendar arithmetic, a business-day /
// court-holiday engine, the federal counting rule (FRCP 6), a jurisdiction registry, an honest
// trust verdict, and one malpractice disclaimer. Authoring those once - correctly, cited, tested -
// and injecting them through the host (never a /js/ import from a blob-loaded plugin; see
// reference_plugin_js_import_blob_gotcha) is what lets the suite be consistent and auditable.
//
// HONESTY POSTURE (Joe's standing orders, applied to law instead of physics).
//   - The COMPUTATION is exact: day counts and comparisons over integer UTC midnights. That earns
//     an EXACT GeoNum verdict with zero drift, and nothing more is claimed for it.
//   - The RULE DATA is cited and dated. Where a jurisdiction's rule is not yet sourced from a
//     primary authority, the registry says so (sourced:false) and the plugin must SURFACE that
//     ambiguity rather than compute a confident-looking wrong answer. A missed deadline is the
//     harm here, so "unknown" is a first-class, visible answer - never a silent default.
//   - Nothing is legal advice. Every plugin renders LEGAL_DISCLAIMER and links each rule to its
//     primary source so a human verifies before relying.
//
// PURE MODULE: no DOM, no window, no imports. Runs identically in the browser and under Node tests.

// =======================================================================================
// 1. EXACT DATE PRIMITIVES  (integer UTC-midnight days - the same basis the K-12 tracker uses)
// =======================================================================================
export const DAY_MS = 86400000;

// Parse a strict YYYY-MM-DD into a UTC-midnight epoch ms, or null. We refuse anything non-strict
// so a malformed trigger date becomes a visible input error, not a silently coerced wrong day.
export function parseISO(iso) {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const t = Date.parse(iso + 'T00:00:00Z');
  if (!Number.isFinite(t)) return null;
  // Reject rolled dates (e.g. 2025-02-30 -> Mar 2): round-trip and compare.
  const d = new Date(t);
  const back = d.getUTCFullYear().toString().padStart(4, '0') + '-'
    + (d.getUTCMonth() + 1).toString().padStart(2, '0') + '-'
    + d.getUTCDate().toString().padStart(2, '0');
  return back === iso ? t : null;
}
export function toISO(ms) {
  const d = new Date(ms);
  return d.getUTCFullYear().toString().padStart(4, '0') + '-'
    + (d.getUTCMonth() + 1).toString().padStart(2, '0') + '-'
    + d.getUTCDate().toString().padStart(2, '0');
}
export function addCalendarDays(iso, n) {
  const t = parseISO(iso);
  return t === null ? null : toISO(t + Math.round(n) * DAY_MS);
}
export function daysBetween(fromISO, toISOStr) {
  const a = parseISO(fromISO), b = parseISO(toISOStr);
  if (a === null || b === null) return null;
  return Math.round((b - a) / DAY_MS);
}
// 0 = Sunday ... 6 = Saturday (UTC).
export function dow(iso) { const t = parseISO(iso); return t === null ? null : new Date(t).getUTCDay(); }
export function isWeekend(iso) { const d = dow(iso); return d === 0 || d === 6; }

// =======================================================================================
// 2. FEDERAL COURT HOLIDAYS  (5 U.S.C. 6103; FRCP 6(a)(6) "legal holiday")
// =======================================================================================
// Rule-computed rather than table-stored so the engine is correct for ANY year, not a snapshot
// that silently expires. Fixed-date holidays observe the federal weekend-shift rule (Sat -> prior
// Friday, Sun -> following Monday) per 5 U.S.C. 6103(b), which is also how courts treat them under
// FRCP 6(a)(6)(A). Nth-weekday holidays never need shifting.
function nthWeekday(year, month0, weekday, n) {
  // n-th (1-based) `weekday` of month0 (0=Jan). weekday 0=Sun..6=Sat.
  const first = Date.UTC(year, month0, 1);
  const firstDow = new Date(first).getUTCDay();
  const offset = (weekday - firstDow + 7) % 7;
  return toISO(first + (offset + (n - 1) * 7) * DAY_MS);
}
function lastWeekday(year, month0, weekday) {
  const last = Date.UTC(year, month0 + 1, 0); // day 0 of next month = last day of this month
  const lastDow = new Date(last).getUTCDay();
  const offset = (lastDow - weekday + 7) % 7;
  return toISO(last - offset * DAY_MS);
}
function observedFixed(year, month0, day) {
  // Federal observation: Sat -> Fri, Sun -> Mon.
  const iso = toISO(Date.UTC(year, month0, day));
  const d = dow(iso);
  if (d === 6) return addCalendarDays(iso, -1);
  if (d === 0) return addCalendarDays(iso, 1);
  return iso;
}
// Federal legal holidays for a year as a Set of observed ISO dates.
export function federalHolidays(year) {
  const s = new Set([
    observedFixed(year, 0, 1),    // New Year's Day
    nthWeekday(year, 0, 1, 3),    // MLK Jr. Day - 3rd Monday Jan
    nthWeekday(year, 1, 1, 3),    // Washington's Birthday - 3rd Monday Feb
    lastWeekday(year, 4, 1),      // Memorial Day - last Monday May
    observedFixed(year, 5, 19),   // Juneteenth (since 2021)
    observedFixed(year, 6, 4),    // Independence Day
    nthWeekday(year, 8, 1, 1),    // Labor Day - 1st Monday Sep
    nthWeekday(year, 9, 1, 2),    // Columbus Day - 2nd Monday Oct
    observedFixed(year, 10, 11),  // Veterans Day
    nthWeekday(year, 10, 4, 4),   // Thanksgiving - 4th Thursday Nov
    observedFixed(year, 11, 25),  // Christmas Day
  ]);
  // Juneteenth only a federal holiday from 2021; drop it earlier so historical counts are honest.
  if (year < 2021) s.delete(observedFixed(year, 5, 19));
  return s;
}

// A HOLIDAY CALENDAR is a function isHoliday(iso)->bool. buildCalendar composes the federal set
// with any extra jurisdiction dates (also cited in the registry). Extra dates are given as either
// fixed {month,day} (observed-shifted) or explicit ISO strings.
export function buildCalendar(extra) {
  const extraFixed = (extra && extra.fixed) || [];
  const extraISO = new Set((extra && extra.dates) || []);
  const cache = {};
  const setFor = (year) => {
    if (cache[year]) return cache[year];
    const s = new Set(federalHolidays(year));
    for (const f of extraFixed) s.add(observedFixed(year, f.month - 1, f.day));
    cache[year] = s;
    return s;
  };
  return function isHoliday(iso) {
    if (extraISO.has(iso)) return true;
    const t = parseISO(iso);
    if (t === null) return false;
    return setFor(new Date(t).getUTCFullYear()).has(iso);
  };
}
export const FEDERAL_CALENDAR = buildCalendar(null);

// =======================================================================================
// 3. BUSINESS-DAY MATH + FRCP RULE 6 COUNTING
// =======================================================================================
export function isBusinessDay(iso, isHoliday) { return !isWeekend(iso) && !(isHoliday || FEDERAL_CALENDAR)(iso); }
export function nextBusinessDay(iso, isHoliday) {
  const hol = isHoliday || FEDERAL_CALENDAR;
  let cur = iso;
  // Guard the loop: at most ~10 iterations are ever needed (weekend + stacked holidays).
  for (let i = 0; i < 15; i++) { if (isBusinessDay(cur, hol)) return cur; cur = addCalendarDays(cur, 1); }
  return cur;
}
export function prevBusinessDay(iso, isHoliday) {
  const hol = isHoliday || FEDERAL_CALENDAR;
  let cur = iso;
  for (let i = 0; i < 15; i++) { if (isBusinessDay(cur, hol)) return cur; cur = addCalendarDays(cur, -1); }
  return cur;
}
// Add N business days, skipping weekends + holidays. Direction follows the sign of n.
export function addBusinessDays(iso, n, isHoliday) {
  const hol = isHoliday || FEDERAL_CALENDAR;
  let cur = iso, step = n >= 0 ? 1 : -1, remaining = Math.abs(Math.round(n));
  while (remaining > 0) { cur = addCalendarDays(cur, step); if (isBusinessDay(cur, hol)) remaining--; }
  return cur;
}
// Count business days in (from, to] - excludes the from day, includes the to day. Exact integer.
export function countBusinessDays(fromISO, toISOStr, isHoliday) {
  const total = daysBetween(fromISO, toISOStr);
  if (total === null) return null;
  const hol = isHoliday || FEDERAL_CALENDAR;
  const step = total >= 0 ? 1 : -1;
  let cur = fromISO, count = 0;
  for (let i = 0; i < Math.abs(total); i++) { cur = addCalendarDays(cur, step); if (isBusinessDay(cur, hol)) count += step; }
  return count;
}

// THE canonical federal deadline computation. Implements FRCP 6(a) + 6(d):
//   6(a)(1)   period stated in days: EXCLUDE the trigger day, count every intervening day.
//   6(a)(1)(C) if the last day is a weekend or legal holiday, roll FORWARD to the next day that
//              is neither. (Backward periods roll BACKWARD - 6(a)(5)/6(a)(1)(C) as applied.)
//   6(a)(2)   period stated in business days: count only business days (some state rules).
//   6(d)      +3 calendar days when service was by mail / other non-personal means, applied
//              BEFORE the final weekend/holiday roll (the standard order of operations).
// Returns a rich object so the caller can show the whole chain, not just the final date.
export function computeDeadline(triggerISO, opts) {
  const o = opts || {};
  const hol = o.isHoliday || FEDERAL_CALENDAR;
  const days = Math.round(o.days || 0);
  const mode = o.countingMode === 'business' ? 'business' : 'calendar';
  const dir = o.direction === 'before' ? -1 : 1; // response deadlines count forward; some (e.g. pretrial) count back
  const t = parseISO(triggerISO);
  if (t === null) return { error: 'Trigger date must be a real YYYY-MM-DD.' };

  const steps = [];
  // 6(d) mail add-on is calendar days regardless of the base counting mode.
  const mailAdd = o.mailService ? Math.round(o.mailDays == null ? 3 : o.mailDays) : 0;

  let raw;
  if (mode === 'business') {
    raw = addBusinessDays(triggerISO, dir * days, hol);
    steps.push(['Count ' + days + ' court (business) day' + (days === 1 ? '' : 's') + ' from ' + triggerISO, raw]);
    if (mailAdd) { raw = addCalendarDays(raw, dir * mailAdd); steps.push(['Add ' + mailAdd + ' calendar days for service by mail (Rule 6(d))', raw]); }
  } else {
    raw = addCalendarDays(triggerISO, dir * (days + mailAdd));
    steps.push(['Count ' + days + ' calendar day' + (days === 1 ? '' : 's') + ' from ' + triggerISO
      + (mailAdd ? ' + ' + mailAdd + ' for service by mail (Rule 6(d))' : ''), raw]);
  }

  // Final roll off any weekend/holiday (6(a)(1)(C)). Forward periods roll forward; backward roll back.
  const rolled = dir >= 0 ? nextBusinessDay(raw, hol) : prevBusinessDay(raw, hol);
  const rollApplied = rolled !== raw;
  if (rollApplied) steps.push([(dir >= 0 ? 'Last day is a weekend/holiday - roll forward to the next court day (Rule 6(a)(1)(C))'
    : 'Last day is a weekend/holiday - roll back to the prior court day'), rolled]);

  return {
    trigger: triggerISO, deadline: rolled, rawDeadline: raw, rollApplied,
    countingMode: mode, direction: dir >= 0 ? 'after' : 'before', days, mailAdd,
    steps, weekdayName: WEEKDAY[dow(rolled)],
  };
}
export const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// =======================================================================================
// 4. HONEST TRUST  (the K-12 exactnessProof, shared)
// =======================================================================================
// The computed outputs are day counts over integer UTC midnights: exact iff every count is a
// finite safe integer. This measures the COMPUTATION, never whether the underlying rule is right.
//
// WHAT THIS IS AND IS NOT - read before touching it.
// The measured quantity is IEEE-754 INTEGER EXACTNESS (Number.isSafeInteger over every reported
// count). It is NOT GeoNum arithmetic drift: no GeoNum struct, zone, shade, drift compartment or
// reduction_precision is involved anywhere in this module. Strictly, then, it should not use GTI
// vocabulary at all - a quantity that is not shades of drift gets its own field with its own name.
//
// UNIT W3 - THE TOKENS AND THE DRIFT FIELD ARE NOW FIXED TOO.
// An earlier pass fixed the synthetic drift on the failing branch (it used to report
// drift_shades = Number.MAX_SAFE_INTEGER, a number invented to force a verdict) but deliberately
// LEFT the 'EXACT' / 'UNRELIABLE' tokens and the drift_shades = 0 on the passing branch, on the
// grounds that shipped plugins concatenate `.trust` into customer text and ~14 harnesses assert
// `r.drift_shades === 0`. That reasoning is now overruled, and here is why:
//
//   js/geonum-trust.js TRUST_FIELDS includes the bare key 'trust' and DRIFT_FIELDS includes
//   'drift_shades'. So a legal plugin that forwards these - and they all do, e.g.
//   `geonum_trust: proof.trust, drift_shades: proof.drift_shades` at
//   plugins/hpc/legal_batch_jurisdiction.plugin.es:300 - lit the shared GTI badge with a green
//   EXACT chip. The user reading that chip is told the platform's GeoNum kernel certified the
//   arithmetic. It did not. Nothing in this module touches the kernel. An integer-exactness check
//   is a fine thing to report; it is not a trust verdict, and it must not borrow one.
//
// A test that asserts the fabricated value is a test pinning a defect, and that is not a reason to
// keep the defect. What IS preserved is the shape: `trust` keeps its KEY (so the five plugins that
// concatenate it into prose print a word rather than "undefined") and only its VALUE changes, to a
// self-describing token that names the property it measured.
//
// THE NEW TOKEN SET. Disjoint from the six canonical GTI names
// (EXACT / PRECISE / VALID / DEGRADED / UNRELIABLE / CANCELLED), and each token names its own
// subject, following the KAHAN_* precedent in wasm/src/euler/engine.rs:507:
//   INTEGER_EXACT    every reported count is a finite IEEE-754 safe integer
//   INTEGER_INEXACT  at least one is not, so the count arithmetic is not exact
// Because these are not verdicts, geonum-trust.js asVerdict() rejects them and the shared badge
// renders the honest "Untracked" - which is the correct reading for a keyless date/integer tool.
//
// drift_shades is REPLACED, not renamed in place, by `gti_tracked: false` plus a reason string.
// A GeoNum-named field holding a number this module never measured is the same laundering as the
// borrowed token, one layer down.
//
// CROSS-OWNER FOLLOW-UP - RESOLVED (verified 2026-07-30). The legal plugin family no longer
// forwards `geonum_trust: proof.trust` / `drift_shades: proof.drift_shades` anywhere: every
// legal plugin now emits `geonum_trust: { tracked: false, status: 'UNTRACKED', reason }` and
// carries the proof token under its own field (integer_exactness / day_count_exactness / ...),
// e.g. plugins/hpc/legal_batch_jurisdiction.plugin.es:301-302. Additionally the shared badge
// (js/geonum-trust.js) now renders any non-canonical token found in a verdict-shaped field as
// an explicitly labelled non-GTI chip, so a future forwarding regression surfaces visibly
// instead of silently lighting a verdict.
export function exactnessProof(values) {
  const exact = values.every((v) => v === null || Number.isSafeInteger(v));
  return {
    exact,
    // The honest name for the property actually proved.
    integer_exact: exact,
    // Self-describing provenance so no downstream reader has to assume this is GTI.
    gti: false,
    measures: 'IEEE-754 integer exactness (Number.isSafeInteger over every reported count) - NOT GeoNum arithmetic drift',
    // GTI, answered explicitly. No GeoNum op ran, so there is no verdict and no drift figure,
    // and the reason travels with the absence so a surface can say "not tracked, because X".
    gti_tracked: false,
    untracked_reason: 'this is an integer/date computation in plain JS; no GeoNum kernel op runs, '
      + 'so no arithmetic drift exists to classify',
    trust: exact ? 'INTEGER_EXACT' : 'INTEGER_INEXACT',
  };
}
// For continuous math (present value, premiums) the outputs are not integer-exact; this reports
// agreement between two independently-computed paths (closed form vs term-by-term loop) to f64
// epsilon.
//
// SAME BOUNDARY AS ABOVE, AND NOW THE SAME FIX. A two-path agreement flag is a BOOLEAN: two
// independent f64 computations either landed within machine epsilon of each other or they did not.
// It has no magnitude, so 'PRECISE' / 'DEGRADED' were borrowed words and the accompanying shade
// figure was invented outright. Both are gone.
//
// THE NEW TOKEN SET, disjoint from the six GTI names and naming its own subject:
//   CROSS_CHECK_AGREES     the closed form and the term-by-term loop agree to f64 epsilon
//   CROSS_CHECK_DISAGREES  they do not, so at least one path is wrong
// `trust` keeps its KEY only so the shipped plugins that concatenate it into prose keep printing a
// word; the value it holds is no longer a verdict, and geonum-trust.js will correctly refuse to
// render it as one.
export function numericTrust(ok) {
  return {
    ok: !!ok,
    gti: false,
    measures: 'agreement of two independent f64 computation paths to machine epsilon - NOT GeoNum arithmetic drift',
    cross_check: ok ? 'AGREES' : 'DISAGREES',
    gti_tracked: false,
    untracked_reason: 'this is a two-path f64 agreement flag; no GeoNum kernel op runs, so no '
      + 'arithmetic drift exists to classify',
    trust: ok ? 'CROSS_CHECK_AGREES' : 'CROSS_CHECK_DISAGREES',
  };
}

// =======================================================================================
// 5. DISCLAIMER + CITATION + AMBIGUITY HELPERS
// =======================================================================================
// The date the embedded legal reference data across the suite was last reviewed. Surfaced on every
// result so a user always sees how current the data is - the one honest failure mode of a keyless,
// embedded-data tool is going stale, so the date is shown, never buried.
export const AS_OF = '2026-07-22';

export const LEGAL_DISCLAIMER = 'This is an automated, in-browser research aid, not legal advice, and its use does not create an attorney-client relationship. It is provided as is, without warranty of any kind, and may contain errors or omissions. It is not a substitute for the independent professional judgment of a licensed attorney in the relevant jurisdiction. Laws, court rules, statutes, rates and guidelines change frequently and vary by jurisdiction and judge; every result links to its primary source - verify there against current law and calendar all critical dates independently. You are solely responsible for verifying every output and for every deadline, filing, calculation and decision in your matters. VaultSync Solutions Inc. and its affiliates disclaim all liability for any loss arising from reliance on these results.';

const escLC = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
export { escLC as esc };

// A clickable citation. Renders the cite text linked to its primary source (rule, statute, or
// case). When no url is known it renders plain text with a visible "(source not linked)" marker
// rather than a dead link - honesty about what we can and cannot point at.
export function cite(text, url, accent) {
  const a = accent || '#7c3aed';
  if (!text) return '';
  if (!url) return '<span title="primary source not linked">' + escLC(text) + '</span>';
  return '<a href="' + escLC(url) + '" target="_blank" rel="noopener noreferrer" style="color:' + a + ';text-decoration:none;">' + escLC(text) + ' &#8599;</a>';
}
// A visible ambiguity / coverage flag. Used whenever a jurisdiction rule is unsourced, a value is
// derived rather than quoted, or a judge/local rule can override. Renders amber, never hidden.
export function ambiguity(note) {
  return '<div style="margin:6px 0;padding:6px 9px;border-left:3px solid #d97706;background:#fffbeb;border-radius:4px;font-size:11.5px;color:#92400e;"><b>Verify:</b> ' + escLC(note) + '</div>';
}
export function disclaimerHTML(asOf) {
  // A VISIBLE bordered notice, not fine print - liability protection only works if it is read.
  // Always carries a dated currency line so the user sees how fresh the reference data is.
  const d = asOf || AS_OF;
  return '<div style="margin-top:10px;border:1px solid #e2e8f0;border-left:3px solid #94a3b8;border-radius:4px;background:#f8fafc;padding:8px 11px;font-size:10.5px;color:#64748b;line-height:1.55;">'
    + '<b style="color:#475569;">Disclaimer:</b> ' + escLC(LEGAL_DISCLAIMER)
    + '<span style="display:block;margin-top:4px;color:#94a3b8;">Reference data reviewed as of ' + escLC(d) + '. Laws change - confirm current effect and dates at each linked primary source.</span>'
    + '</div>';
}

// Render a cited legal authority as a clickable link WITH an effective / version date. This is the
// canonical way a plugin should surface a law it speaks to: the text links to the primary source,
// and the date says which version. Falls back to a plain dated label when no url is known (honest).
export function citeDated(text, url, dateLabel, accent) {
  const link = cite(text, url, accent);
  return link + (dateLabel ? ' <span style="color:#94a3b8;font-size:0.95em;">(' + escLC(dateLabel) + ')</span>' : '');
}

// =======================================================================================
// 6. JURISDICTION REGISTRY
// =======================================================================================
// The 51 US jurisdictions (federal + 50 states + DC), each with court levels and - where sourced
// from a primary authority - extra court holidays and the counting-rule citation. `sourced:false`
// is an HONEST marker: the deadline math still runs on the federal calendar, but the plugin must
// flag that this jurisdiction's specific holidays/counting rule were not primary-verified. This is
// the "identify ambiguity" discipline: we never present an unsourced jurisdiction as authoritative.
//
// Seeded now with primary-cited entries for the federal courts and a first set of high-volume
// states; the remainder are present (so every jurisdiction is selectable) but flagged unsourced
// until their rule is added. Expansion is data-only.
export const JURISDICTIONS = {
  US: { name: 'Federal (U.S. District Courts)', courts: ['District Court', 'Court of Appeals', 'Bankruptcy Court'],
    rule: 'Fed. R. Civ. P. 6', ruleUrl: 'https://www.law.cornell.edu/rules/frcp/rule_6',
    calendar: FEDERAL_CALENDAR, sourced: true,
    note: 'FRCP 6(a) counting and 6(d) mail add-on, over the 5 U.S.C. 6103 federal holidays. Individual judges local rules and CM/ECF standing orders can modify service and page timing - not deadline day-counts.' },
};
// State scaffold. `extra` holds primary-cited court-holiday additions where known. When `sourced`
// is false the entry exists for selection but carries only the federal calendar + an ambiguity flag.
const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky',
  LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
  NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};
// Primary-cited state counting-rule entries (expandable). Each names the state civil-procedure
// time-computation rule and links it. Court-holiday additions beyond the federal set go in `fixed`.
const STATE_SOURCED = {
  CA: { rule: 'Cal. Code Civ. Proc. 12, 12a, 12b + Gov. Code 6700', ruleUrl: 'https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=CCP&sectionNum=12',
    note: 'CCP 12 excludes the first day and includes the last; 12a rolls a last day on a holiday to the next court day; Gov. Code 6700 lists state holidays (adds Cesar Chavez Day Mar 31, day after Thanksgiving).',
    extra: { fixed: [{ month: 3, day: 31 }, { month: 11, day: 29 }] } },
  NY: { rule: 'N.Y. Gen. Constr. Law 20-25-a; CPLR 2103', ruleUrl: 'https://www.nysenate.gov/legislation/laws/GCT/25-A',
    note: 'General Construction Law 25-a rolls a last day on a public holiday to the next business day; CPLR 2103 sets service add-ons (5 days by mail, differing from the federal 3).',
    extra: { fixed: [{ month: 2, day: 12 }] } },
  TX: { rule: 'Tex. R. Civ. P. 4; Tex. Gov. Code 662.021', ruleUrl: 'https://www.txcourts.gov/media/1454529/trcp.pdf',
    note: 'TRCP 4 excludes the first day, includes the last, and rolls Saturday/Sunday/legal-holiday last days to the next day; +3 days for service by mail under TRCP 21a.', extra: null },
  FL: { rule: 'Fla. R. Jud. Admin. 2.514; Fla. R. Civ. P. 1.090', ruleUrl: 'https://www.floridabar.org/rules/ctproc/',
    note: 'Rule 2.514 mirrors the federal FRCP 6(a) counting method; 5 additional days for service by mail under Rule 1.090(e).', extra: null },
};

for (const pc of Object.keys(STATE_NAMES)) {
  const src = STATE_SOURCED[pc];
  JURISDICTIONS[pc] = {
    name: STATE_NAMES[pc], courts: ['Trial Court', 'Appellate Court', 'Supreme Court'],
    rule: src ? src.rule : null, ruleUrl: src ? src.ruleUrl : null,
    calendar: src && src.extra ? buildCalendar(src.extra) : FEDERAL_CALENDAR,
    sourced: !!src, note: src ? src.note : null,
  };
}

export function jurisdiction(code) { return JURISDICTIONS[String(code || '').toUpperCase()] || null; }
export function jurisdictionOptions() {
  return Object.keys(JURISDICTIONS).map((k) => ({ v: k, l: JURISDICTIONS[k].name + (JURISDICTIONS[k].sourced ? '' : ' (rule unsourced)') }));
}
